/**
 * The Aplikacja tab: three preferences, none of which reaches the backend.
 *
 * **Motyw** and **Język** are this browser's, which is what the tab's lede says. **Język treści
 * ćwiczeń** is the one with reach: it decides the `lang` every exercise-bearing request sends, so
 * changing it re-keys those caches and the library, the builder and both calendars come back in the
 * other language. Two options only — Polski and English — and no "follow the app": the owner's
 * decision, and the store underneath still follows the interface language until a choice is made.
 *
 * Nothing here reads. The tab has no pending, error or empty state because there is no answer to
 * wait for — every value is already in the browser when the tab mounts.
 */

import { useTranslation } from 'react-i18next';

import {
  parseThemePreference,
  THEME_PREFERENCES,
  useThemeStore,
  type ThemePreference,
} from '~/shared/design-system/theme';
import {
  changeLanguage,
  resolveLanguage,
  SUPPORTED_LANGUAGES,
  type LanguageCode,
} from '~/shared/i18n';
import { setContentLanguage, useContentLanguage } from '~/shared/i18n/contentLanguage';

import { MonitorIcon, MoonIcon, SunIcon } from './AccountIcons';
import { SegmentedChoice, type SegmentedOption } from '~/shared/ui/SegmentedChoice';
import screen from './AccountScreen.module.css';

const THEME_ICON: Readonly<
  Record<ThemePreference, (props: { className?: string }) => React.JSX.Element>
> = {
  light: SunIcon,
  dark: MoonIcon,
  system: MonitorIcon,
};

export function AppTab() {
  const { t, i18n } = useTranslation();
  // Narrow selectors, both: this tab re-renders when the preference changes and for nothing else.
  // Subscribing to the whole store would re-render it on every `prefers-color-scheme` event the OS
  // emits, which is a change this tab does not draw.
  const preference = useThemeStore((state) => state.preference);
  const setPreference = useThemeStore((state) => state.setPreference);
  const contentLanguage = useContentLanguage();

  const themeOptions: readonly SegmentedOption<ThemePreference>[] = THEME_PREFERENCES.map(
    (value) => ({
      value,
      label: t(`web:account.app.theme.${value}`),
      Icon: THEME_ICON[value],
    }),
  );

  const languageOptions: readonly SegmentedOption<LanguageCode>[] = SUPPORTED_LANGUAGES.map(
    (value) => ({ value, label: t(`web:account.app.language.${value}`) }),
  );

  return (
    <div className={screen.inner}>
      <div className={screen.lede}>
        <h2 className={screen.h2}>{t('web:account.app.heading')}</h2>
        <p className={screen.ledeText}>{t('web:account.app.lede')}</p>
      </div>

      <div className={`${String(screen.block)} ${String(screen.rows)}`}>
        <div className={screen.row}>
          <div className={screen.rowText}>
            <b className={screen.rowTitle}>{t('web:account.app.theme.label')}</b>
          </div>
          <SegmentedChoice
            legend={t('web:account.app.theme.label')}
            options={themeOptions}
            value={preference}
            onChange={(value) => {
              // Narrowed through the contract rather than trusted: a value this app does not ship
              // resolves to `system` instead of being written into the store.
              setPreference(parseThemePreference(value));
            }}
          />
        </div>

        <div className={screen.row}>
          <div className={screen.rowText}>
            <b className={screen.rowTitle}>{t('web:account.app.language.label')}</b>
          </div>
          <SegmentedChoice
            legend={t('web:account.app.language.label')}
            options={languageOptions}
            // `i18n.language` can be a REGIONAL tag on a Polish Windows, and this row offers the
            // two bundles the app ships — reduced through the same rule the content language reads,
            // so the two rows cannot disagree about what `pl-PL` is.
            value={resolveLanguage(i18n.language)}
            onChange={(value) => {
              void changeLanguage(value);
            }}
          />
        </div>

        <div className={screen.row}>
          <div className={screen.rowText}>
            <b className={screen.rowTitle}>{t('web:account.app.contentLanguage.label')}</b>
            <span className={screen.rowNote}>{t('web:account.app.contentLanguage.note')}</span>
          </div>
          <SegmentedChoice
            legend={t('web:account.app.contentLanguage.label')}
            // The same two words, and deliberately the same list: this row offers the same two
            // languages the row above does, and a second spelling of „Polski" is how two controls
            // come to disagree.
            options={languageOptions}
            value={contentLanguage}
            onChange={setContentLanguage}
          />
        </div>
      </div>
    </div>
  );
}
